import { Component, ErrorInfo } from 'react';

import { logger } from '../../logging/logging.ts';
import { ErrorBoundaryProps, ErrorBoundaryState } from './types.ts';

const log = logger();

export default class ErrorBoundary<Props = void> extends Component<
  ErrorBoundaryProps<Props>,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps<Props>) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError } = this.props;

    log.fatal({ name: 'ErrorBoundary', error, errorInfo });
    onError?.(error);
  }

  render() {
    const { hasError, error } = this.state;
    const { children, Fallback, fallbackProps = {} } = this.props;

    if (!hasError) return children;
    const errorString = `${error.message}\n\n${error.stack}`;
    const props = {
      error,
      errorString,
      ...(fallbackProps as Props),
    };

    return <Fallback {...props} />;
  }
}
